"use client";

import { SendBotLogo } from "@sendbot/ui/SendBotLogo";
import {
  Github,
  Instagram,
  Linkedin,
  MessageSquare,
  Twitter,
} from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { ConsentPreferencesButton } from "@/components/analytics/ConsentPreferencesButton";
import { Link as I18nLink } from "@/i18n/routing";
import { useRouting } from "@/lib/navigation";

const footerLinkClass =
  "text-sm text-muted-foreground transition-colors hover:text-foreground";

const socialLinks = [
  {
    href: "https://twitter.com/sendbot",
    Icon: Twitter,
    eventType: "twitter",
    label: "Twitter",
  },
  {
    href: "https://linkedin.com/company/sendbot",
    Icon: Linkedin,
    eventType: "linkedin",
    label: "LinkedIn",
  },
  {
    href: "https://github.com/sendbot",
    Icon: Github,
    eventType: "github",
    label: "GitHub",
  },
  {
    href: "https://www.instagram.com/sendbot.co/",
    Icon: Instagram,
    eventType: "instagram",
    label: "Instagram",
  },
  {
    href: "https://whatsapp.com/channel/0029VaTgsNMKQuJRS306UC1Q",
    Icon: MessageSquare,
    eventType: "whatsapp",
    label: "WhatsApp",
  },
] as const;

export function Footer() {
  const t = useTranslations("Footer");
  const navT = useTranslations("Navbar");
  const { routes, externalRoutes } = useRouting();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-border/60 bg-background">
      <div className="container mx-auto px-6 py-14 md:px-8 md:py-16">
        <div className="mb-14 grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-5">
            <div className="inline-flex flex-col items-start gap-2">
              <I18nLink
                href={routes.home}
                className="flex items-center space-x-3 transition-opacity hover:opacity-90"
                aria-label={navT("home")}
              >
                <SendBotLogo variant="full" size={36} color="white" />
              </I18nLink>
              <div
                className="flex items-center gap-2 text-[11px] font-medium tracking-[0.12em] text-muted-foreground/80"
                role="group"
                aria-label="SendBot by Ciatto"
              >
                <span className="h-px w-5 bg-border/80" aria-hidden="true" />
                <span>
                  by{" "}
                  <span className="font-semibold tracking-[0.08em] text-foreground/70">
                    Ciatto
                  </span>
                </span>
              </div>
            </div>
            <p className="max-w-xs text-base text-muted-foreground">
              {t("slogan")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("follow_instagram")}
            </p>
            <div className="flex gap-2 pt-1">
              {socialLinks.map(({ href, Icon, eventType, label }) => (
                <Button
                  key={href}
                  variant="ghost"
                  size="icon"
                  className="rounded-full text-muted-foreground hover:bg-accent/70 hover:text-foreground"
                  asChild
                >
                  <Link
                    href={href}
                    aria-label={`${label} SendBot`}
                    rel="noopener noreferrer"
                    target="_blank"
                    data-umami-event="social-click"
                    data-umami-event-type={eventType}
                  >
                    <Icon className="h-4 w-4" />
                  </Link>
                </Button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-5 text-base font-semibold">{t("product")}</h3>
            <ul className="space-y-3">
              <li>
                <I18nLink href={routes.status} className={footerLinkClass}>
                  {t("links.status")}
                </I18nLink>
              </li>
              <li>
                <I18nLink href={routes.tools} className={footerLinkClass}>
                  {t("links.tools")}
                </I18nLink>
              </li>
              <li>
                <I18nLink href={routes.enterprise} className={footerLinkClass}>
                  {t("links.enterprise")}
                </I18nLink>
              </li>
              <li>
                <I18nLink href={routes.gov} className={footerLinkClass}>
                  {t("links.gov")}
                </I18nLink>
              </li>
              <li>
                <I18nLink href={routes.pricing} className={footerLinkClass}>
                  {t("links.pricing")}
                </I18nLink>
              </li>
              <li>
                <I18nLink
                  href={routes.integrations}
                  className={footerLinkClass}
                >
                  {t("links.integrations")}
                </I18nLink>
              </li>
              <li>
                <I18nLink href="/mcp" className={footerLinkClass}>
                  Sendbot MCP
                </I18nLink>
              </li>
              <li>
                <I18nLink href={routes.cases} className={footerLinkClass}>
                  {t("links.cases")}
                </I18nLink>
              </li>
              <li>
                <I18nLink href={routes.demo} className={footerLinkClass}>
                  {t("links.demo")}
                </I18nLink>
              </li>
              <li>
                <a
                  href={externalRoutes.docs}
                  className={footerLinkClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("links.docs")}
                </a>
              </li>
              <li>
                <I18nLink href={routes.resources} className={footerLinkClass}>
                  {t("links.resources")}
                </I18nLink>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-5 text-base font-semibold">{t("company")}</h3>
            <ul className="space-y-3">
              <li>
                <I18nLink href={routes.about} className={footerLinkClass}>
                  {t("links.about")}
                </I18nLink>
              </li>
              <li>
                <I18nLink href={routes.partners} className={footerLinkClass}>
                  {t("links.partners")}
                </I18nLink>
              </li>
              <li>
                <Link
                  href={externalRoutes.blog}
                  className={footerLinkClass}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("links.blog")}
                </Link>
              </li>
              <li>
                <I18nLink href={routes.support} className={footerLinkClass}>
                  {t("links.support")}
                </I18nLink>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-5 text-base font-semibold">{t("legal")}</h3>
            <ul className="space-y-3">
              <li>
                <I18nLink href={routes.terms} className={footerLinkClass}>
                  {t("links.terms")}
                </I18nLink>
              </li>
              <li>
                <I18nLink href={routes.privacy} className={footerLinkClass}>
                  {t("links.privacy")}
                </I18nLink>
              </li>
              <li>
                <I18nLink href={routes.security} className={footerLinkClass}>
                  {t("links.security")}
                </I18nLink>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border/60 pt-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="text-sm text-muted-foreground">
              {t("copyright", { year: currentYear })}
            </p>
            <div className="flex flex-wrap justify-center gap-5 md:gap-8">
              <ConsentPreferencesButton />
              <I18nLink href={routes.terms} className={footerLinkClass}>
                {t("links.terms")}
              </I18nLink>
              <I18nLink href={routes.privacy} className={footerLinkClass}>
                {t("links.privacy")}
              </I18nLink>
              <Link
                href="https://studio.sendbot.co/signin"
                className={footerLinkClass}
                data-umami-event="cta-click"
                data-umami-event-name="footer-login"
              >
                {t("login")}
              </Link>
              <Link
                href="https://studio.sendbot.co/register"
                className={`${footerLinkClass} font-medium text-foreground`}
                data-umami-event="cta-click"
                data-umami-event-name="footer-signup"
              >
                {t("signup")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
